import React, { useState, useEffect, useMemo } from 'react';
import { 
  FaChartLine, 
  FaArrowUp, 
  FaArrowDown, 
  FaShieldAlt, 
  FaExclamationTriangle, 
  FaSync,
  FaSearch,
  FaSortAmountDown,
  FaSortAmountUp,
  FaLock,
  FaSignInAlt,
  FaPhone,
  FaEnvelope,
  FaCrown,
  FaCalendarAlt,
  FaRupeeSign,
  FaFilePdf,
  FaExternalLinkAlt,
  FaDownload,
  FaGoogle,
  FaArrowRight
} from 'react-icons/fa';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Header from '../Header';
import Footer from '../Footer';
import axios from 'axios';

const DividendGrowthPortfolio = () => {
  // ==================== START: LOGIN PROTECTION LOGIC ====================
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [checkingLogin, setCheckingLogin] = useState(true);
  const [userData, setUserData] = useState(null);
  const [contentAccess, setContentAccess] = useState({ DividendGrowthPortfolio: 0 });


    // Page metadata========================================================
    const [pageMetadata, setPageMetadata] = useState({
      page_names: 'IPO Research & Analysis',
      description: 'Comprehensive IPO data from SEBI Registered Research Analyst'
    });
    // Add this fetch function (around line 60-80)
    const fetchPageMetadata = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL || 'https://api.marketmaestroovip.in'}/api/page-metadata/public/page8`);
        if (response.data) {
          setPageMetadata({
            page_names: response.data.page_name, // Using title from database
            description: response.data.description
          });
        }
      } catch (error) {
        console.error('Error fetching page metadata:', error);
        // Keep default values if fetch fails
      }
    };
  
    // Add this inside your existing useEffect or create a new one (around line 100-110)
    useEffect(() => {
      if (isLoggedIn && !checkingLogin && contentAccess.DividendGrowthPortfolio === 1) {
        fetchPageMetadata(); // Add this line
      }
    }, [isLoggedIn, checkingLogin, contentAccess.DividendGrowthPortfolio]);
    // Metadata End===========================================================
  
  const checkLoginStatus = async () => {
    try {
      const accessToken = localStorage.getItem('accessToken');
      const userDataStr = localStorage.getItem('user');
      const loggedIn = !!(accessToken && userDataStr);
      setIsLoggedIn(loggedIn);
      
      if (loggedIn) {
        const user = JSON.parse(userDataStr);
        setUserData(user);
        await fetchContentAccess(user.id);
      } else {
        setUserData(null);
        setContentAccess({ DividendGrowthPortfolio: 0 });
      }
      
      setCheckingLogin(false);
    } catch (error) {
      console.error('Error checking login status:', error);
      setCheckingLogin(false);
    }
  };
  
  const fetchContentAccess = async (userId) => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'https://api.marketmaestroovip.in';
      const response = await axios.get(`${apiUrl}/api/content-access/${userId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
        }
      });
      
      if (response.data.success) {
        setContentAccess(response.data.data);
      } else {
        setContentAccess({ DividendGrowthPortfolio: 0 });
      }
    } catch (error) {
      console.error('Error fetching content access:', error);
      if (error.response?.status === 404) {
        setContentAccess({ DividendGrowthPortfolio: 0 });
      }
    }
  };
  
  useEffect(() => {
    checkLoginStatus();
    
    const handleStorageChange = () => {
      checkLoginStatus();
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);
  // ==================== END: LOGIN PROTECTION LOGIC ====================
  
  // ==================== ORIGINAL COMPONENT STATE ====================
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [ipoSearchTerm, setIpoSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'RECO DATE', direction: 'desc' }); // Default sort by Reco Date descending
  const [columnHeaders, setColumnHeaders] = useState([]);
  const [ipoHeaders, setIpoHeaders] = useState([]);
  const [processedData, setProcessedData] = useState({ 
    stocks: [], 
    ipos: [], 
    researchReports: [],
    googleForm: null,
    disclosures: [], 
    columnHeaders: [], 
    ipoHeaders: [] 
  });

  const API_URL = 'https://sheets.googleapis.com/v4/spreadsheets/11TbPM7oi9IX4UNKAWeQLzuOq1tKVVw7JTx_EWxznMj4/values/MM%20DIVIDEND%20GROWTH%20PORTFOLIO?key=AIzaSyBBDGaGd_H-xl8tjzlIZC-Yx2QLMM5FVUA';

  useEffect(() => {
    if (isLoggedIn && !checkingLogin && contentAccess.DividendGrowthPortfolio === 1) {
      fetchPortfolioData();
    } else {
      if (isLoggedIn && !checkingLogin) {
        setLoading(false);
      }
    }
  }, [isLoggedIn, checkingLogin, contentAccess.DividendGrowthPortfolio]);

  // Auto-refresh every 60 seconds
  useEffect(() => {
    if (isLoggedIn && !checkingLogin && contentAccess.DividendGrowthPortfolio === 1) {
      const intervalId = setInterval(() => {
        fetchPortfolioData();
      }, 60000);
      
      return () => clearInterval(intervalId);
    }
  }, [isLoggedIn, checkingLogin, contentAccess.DividendGrowthPortfolio]);

  // Process data when data changes
  useEffect(() => {
    if (data && data.values) {
      const result = processData(data.values);
      setProcessedData(result);
      setColumnHeaders(result.columnHeaders);
      setIpoHeaders(result.ipoHeaders);
    }
  }, [data]);

  const fetchPortfolioData = async () => {
    try {
      setLoading(true);
      const response = await fetch(API_URL);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch data: ${response.status} ${response.statusText}`);
      }
      
      const result = await response.json();
      
      if (!result.values) {
        throw new Error('No data found in the response');
      }
      
      setData(result);
      setLastUpdated(new Date());
      setLoading(false);
      
    } catch (err) {
      console.error('Error fetching dividend portfolio data:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  // Process stock data, IPO data, research reports, google form and disclosures
  const processData = (values) => {
    if (!values) {
      return { stocks: [], ipos: [], researchReports: [], googleForm: null, disclosures: [], columnHeaders: [], ipoHeaders: [] };
    }

    let stocks = [];
    let ipos = [];
    let researchReports = [];
    let googleForm = null;
    let disclosures = [];
    let stockHeaders = [];
    let ipoHeaders = [];
    let foundStockTable = false;
    let foundIPOTable = false;
    
    for (let i = 0; i < values.length; i++) {
      const row = values[i];
      if (!row || row.length === 0) continue;
      
      const rowString = row.join('').toUpperCase();
      const hasSno = rowString.includes('S.NO') || rowString.includes('SNO');
      const hasTicker = rowString.includes('TICKER');
      const hasStockName = rowString.includes('STOCK NAME') || rowString.includes('STOCKNAME');
      const hasIPOName = rowString.includes('IPO NAME');
      const hasNew = rowString.includes('NEW');
      
      // Check for Google Form link
      const hasGoogleFormLink = row.some(cell => 
        cell && typeof cell === 'string' && cell.includes('forms.gle')
      );
      
      if (hasGoogleFormLink) {
        const formLink = row.find(cell => 
          cell && typeof cell === 'string' && cell.includes('forms.gle')
        );
        
        let formTitle = '';
        if (i > 0 && values[i - 1]) {
          const prevRow = values[i - 1];
          const titleCell = prevRow.find(cell => 
            cell && typeof cell === 'string' && 
            (cell.includes('FILL THIS') || cell.includes('FORM') || cell.includes('Google Form'))
          );
          if (titleCell) {
            formTitle = titleCell;
          }
        }
        
        if (!formTitle) {
          const titleInRow = row.find(cell => 
            cell && typeof cell === 'string' && 
            (cell.includes('FILL THIS') || cell.includes('FORM') || cell.includes('Google Form'))
          );
          if (titleInRow) {
            formTitle = titleInRow;
          }
        }
        
        if (formLink) {
          googleForm = {
            title: formTitle || 'Google Form',
            link: formLink
          };
          console.log('Found Google Form:', googleForm);
        }
        continue;
      }
      
      // Check for research reports (Google Drive links)
      const hasDriveLink = row.some(cell => 
        cell && typeof cell === 'string' && cell.includes('drive.google.com')
      );
      
      if (hasDriveLink) {
        const companyName = row[0] ? row[0].toString().trim() : '';
        const reportLink = row.find(cell => 
          cell && typeof cell === 'string' && cell.includes('drive.google.com')
        ) || '';
        
        if (companyName && reportLink && 
            !companyName.includes('COMING SOON') && 
            !companyName.includes('S.NO') &&
            !companyName.includes('TICKER') &&
            companyName.length > 2) {
          
          const isDuplicate = researchReports.some(r => r.companyName === companyName);
          if (!isDuplicate) {
            researchReports.push({
              companyName: companyName,
              reportLink: reportLink,
              reportDate: new Date().toLocaleDateString(),
              additionalInfo: row[1] || row[2] || row[3] || ''
            });
          }
        }
        continue;
      }
      
      // Process STOCK table headers and data
      if (hasSno && hasTicker && hasStockName && !hasIPOName && !foundStockTable) {
        foundStockTable = true;
        
        // Clean stock headers
        stockHeaders = row.map(h => {
          if (!h) return '';
          let cleaned = h.toString().trim();
          if (cleaned.split(' ').length > 3) {
            const words = cleaned.split(' ').filter(letter => letter.length > 0);
            const reconstructed = words.join('');
            if (reconstructed.toUpperCase().includes('STOCKNAME')) {
              return 'STOCK NAME';
            } else if (reconstructed.toUpperCase().includes('RECODATE')) {
              return 'RECO DATE';
            } else if (reconstructed.toUpperCase().includes('SNO')) {
              return 'S.NO';
            }
          }
          return cleaned;
        });
        
        // Process stock data rows
        for (let j = i + 1; j < values.length; j++) {
          const stockRow = values[j];
          if (!stockRow || stockRow.length === 0) continue;
          
          // Check if we've reached IPO section
          const nextRowString = stockRow.join('').toUpperCase();
          if (nextRowString.includes('IPO NAME') && nextRowString.includes('S.NO')) {
            break;
          }
          
          // Skip "COMING SOON" rows
          if (nextRowString.includes('COMING SOON')) {
            continue;
          }
          
          // Check if this is a valid stock data row
          const snoValue = stockRow[2] || '';
          const sno = parseInt(snoValue.toString().trim());
          if (isNaN(sno) || sno < 1) continue;
          
          // Check if row has any valid data
          const hasValidData = stockRow.some((cell, idx) => {
            if (idx < stockHeaders.length && stockHeaders[idx]) {
              return cell !== undefined && cell !== null && cell.toString().trim() !== '';
            }
            return false;
          });
          
          if (!hasValidData) continue;
          
          // Create stock object
          const stockData = {};
          let hasData = false;
          let isNew = false;
          
          stockHeaders.forEach((header, index) => {
            if (header && stockRow[index] !== undefined && stockRow[index] !== null) {
              const value = stockRow[index].toString().trim();
              
              // Check for "NEW" column
              if (header.toLowerCase().includes('new')) {
                isNew = value.toLowerCase().includes('new');
              }
              
              if (value !== '') {
                stockData[header] = value;
                hasData = true;
              }
            }
          });
          
          if (hasData) {
            stocks.push({
              id: sno,
              data: stockData,
              headers: Object.keys(stockData),
              isNew: isNew,
              originalRowIndex: j // Preserve original row index for stable ordering
            });
          }
        }
      }
      
      // Process IPO table headers and data
      if (hasSno && hasIPOName && !foundIPOTable) {
        foundIPOTable = true;
        
        // Clean IPO headers
        ipoHeaders = row.map(h => {
          if (!h) return '';
          let cleaned = h.toString().trim();
          if (cleaned.split(' ').length > 3) {
            const words = cleaned.split(' ').filter(letter => letter.length > 0);
            const reconstructed = words.join('');
            if (reconstructed.toUpperCase().includes('IPONAME')) {
              return 'IPO NAME';
            } else if (reconstructed.toUpperCase().includes('CURRENTPRICE')) {
              return 'CURRENT PRICE';
            }
          }
          return cleaned;
        });
        
        // Process IPO data rows
        for (let j = i + 1; j < values.length; j++) {
          const ipoRow = values[j];
          if (!ipoRow || ipoRow.length === 0) continue;
          
          // Check if we've reached research report section
          const nextRowString = ipoRow.join('').toUpperCase();
          if (nextRowString.includes('MARKET MAESTROO RESERACH REPORT') || 
              nextRowString.includes('RESEARCH REPORT') ||
              (ipoRow[0] && ipoRow[0].toString().trim().includes('http'))) {
            break;
          }
          
          // Skip empty rows or rows with only S.NO
          const snoValue = ipoRow[2] || '';
          const sno = parseInt(snoValue.toString().trim());
          if (isNaN(sno) || sno < 1) continue;
          
          // Check if row has IPO name
          const ipoName = ipoRow[3] || '';
          if (!ipoName || ipoName.toString().trim() === '') continue;
          
          // Check if row has any valid data
          const hasValidData = ipoRow.some((cell, idx) => {
            if (idx < ipoHeaders.length && ipoHeaders[idx]) {
              return cell !== undefined && cell !== null && cell.toString().trim() !== '';
            }
            return false;
          });
          
          if (!hasValidData) continue;
          
          // Create IPO object
          const ipoData = {};
          let hasData = false;
          let isNew = false;
          
          ipoHeaders.forEach((header, index) => {
            if (header && ipoRow[index] !== undefined && ipoRow[index] !== null) {
              const value = ipoRow[index].toString().trim();
              
              // Check for "NEW" column
              if (header.toLowerCase().includes('new')) {
                isNew = value.toLowerCase().includes('new');
              }
              
              if (value !== '') {
                ipoData[header] = value;
                hasData = true;
              }
            }
          });
          
          if (hasData) {
            ipos.push({
              id: sno,
              data: ipoData,
              headers: Object.keys(ipoData),
              isNew: isNew
            });
          }
        }
      }
      
      // Process disclosures only (ignore research reports, Google forms, etc.)
      if (row.length > 0 && row[0]) {
        const text = row[0].toString().trim();
        
        // Skip research reports and Google forms
        const lowerText = text.toLowerCase();
        if (lowerText.includes('market maestroo reseach report') ||
            lowerText.includes('research report') ||
            lowerText.includes('fill this google form') ||
            lowerText.includes('http') ||
            lowerText.includes('drive.google.com') ||
            lowerText.includes('forms.gle')) {
          continue;
        }
        
        if (text.includes('SEBI REGISTERED')) {
          disclosures.push({ 
            type: 'registration', 
            text: text.replace(/\r\n/g, '\n') 
          });
        } else if (text.includes('RISK DISCLOSURE')) {
          disclosures.push({ 
            type: 'risk', 
            text: text.replace(/\r\n/g, '\n') 
          });
        } else if (text.includes('DISCLAIMER') && !text.includes('GOOGLE FORM')) {
          disclosures.push({ 
            type: 'disclaimer', 
            text: text.replace(/\r\n/g, '\n') 
          });
        } else if (text.includes('INVESTOR CAUTION')) {
          disclosures.push({ 
            type: 'caution', 
            text: text.replace(/\r\n/g, '\n') 
          });
        }
      }
    }
    
    // Clean headers for display
    const cleanStockHeaders = stockHeaders
      .filter(h => h && h.trim() !== '')
      .map(h => normalizeHeaderName(h));
      
    const cleanIPOHeaders = ipoHeaders
      .filter(h => h && h.trim() !== '')
      .map(h => normalizeHeaderName(h));
    
    return { 
      stocks, 
      ipos, 
      researchReports,
      googleForm,
      disclosures, 
      columnHeaders: cleanStockHeaders,
      ipoHeaders: cleanIPOHeaders 
    };
  };

  // Normalize header names (fix "S T O C K N A M E" to "STOCK NAME")
  const normalizeHeaderName = (header) => {
    if (!header) return '';
    
    let cleaned = header.trim();
    
    // If header has spaces between letters (like "S T O C K N A M E")
    if (cleaned.split(' ').length > 3 && cleaned.replace(/\s+/g, '').length > 5) {
      const letters = cleaned.split(' ').filter(l => l.length > 0);
      const combined = letters.join('');
      
      // Check for common patterns
      const upperCombined = combined.toUpperCase();
      if (upperCombined.includes('STOCKNAME')) return 'STOCK NAME';
      if (upperCombined.includes('RECODATE')) return 'RECO DATE';
      if (upperCombined.includes('RECOPRICE')) return 'RECO PRICE';
      if (upperCombined.includes('SNO') || upperCombined.includes('S.NO')) return 'S.NO';
      if (upperCombined.includes('TICKER')) return 'TICKER';
      if (upperCombined.includes('CMP')) return 'CMP';
      if (upperCombined.includes('RETURN')) return 'RETURN';
      if (upperCombined.includes('IPONAME')) return 'IPO NAME';
      if (upperCombined.includes('CURRENTPRICE')) return 'CURRENT PRICE';
      if (upperCombined.includes('NEW')) return 'NEW';
    }
    
    return cleaned;
  };

  // Format header names for display (clean version)
  const formatHeaderName = (header) => {
    const normalized = normalizeHeaderName(header);
    
    // Common header mappings
    const headerMap = {
      'S.NO': 'S.No',
      'TICKER': 'Ticker',
      'STOCK NAME': 'Stock Name',
      'RECO PRICE': 'Reco Price',
      'CMP': 'CMP',
      'RETURN': 'Return',
      'RECO DATE': 'Reco Date',
      'IPO NAME': 'IPO Name',
      'DATE': 'Date',
      'IPO PRICE': 'IPO Price',
      'CURRENT PRICE': 'Current Price',
      'RATING': 'Rating',
      'BOOKLET': 'Booklet',
      'LINK': 'Link',
      'NEW': 'New'
    };
    
    if (headerMap[normalized]) {
      return headerMap[normalized];
    }
    
    // Format other headers nicely
    return normalized
      .replace(/_/g, ' ')
      .replace(/([A-Z])/g, ' $1')
      .replace(/\./g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/\b\w/g, l => l.toUpperCase());
  };

  // Check if value is numeric
  const isNumeric = (value) => {
    if (typeof value === 'number') return true;
    if (typeof value !== 'string') return false;
    const clean = value.replace(/[₹%,]/g, '').trim();
    return !isNaN(clean) && !isNaN(parseFloat(clean));
  };

  // Format value for display
  const formatValue = (value, header = '') => {
    if (value === undefined || value === null || value === '') return 'N/A';
    
    const strValue = value.toString();
    const cleanValue = strValue.replace(/[₹%,]/g, '').trim();
    const isNum = !isNaN(cleanValue) && !isNaN(parseFloat(cleanValue));
    
    if (isNum) {
      const numValue = parseFloat(cleanValue);
      const headerUpper = normalizeHeaderName(header).toUpperCase();
      
      if (headerUpper.includes('%') || headerUpper.includes('RETURN') || headerUpper.includes('YIELD')) {
        return `${numValue >= 0 ? '+' : ''}${numValue.toFixed(2)}%`;
      } else if (headerUpper.includes('PRICE') || headerUpper.includes('AMOUNT') || headerUpper.includes('VALUE') || headerUpper.includes('CMP')) {
        return `₹${numValue.toFixed(2)}`;
      } else if (numValue % 1 === 0) {
        return numValue.toString();
      } else {
        return numValue.toFixed(2);
      }
    }
    
    return strValue;
  };

  // Helper function to parse date from various formats
  const parseDate = (dateString) => {
    if (!dateString || dateString === '') return null;
    
    // Try to parse as DD/MM/YYYY or DD-MM-YYYY or DD.MM.YYYY
    const cleaned = dateString.toString().trim();
    
    // Check for common date formats
    let day, month, year;
    
    // Format: DD/MM/YYYY
    if (cleaned.includes('/')) {
      const parts = cleaned.split('/');
      if (parts.length === 3) {
        day = parseInt(parts[0]);
        month = parseInt(parts[1]) - 1;
        year = parseInt(parts[2]);
        const date = new Date(year, month, day);
        if (!isNaN(date.getTime())) return date;
      }
    }
    
    // Format: DD-MM-YYYY
    if (cleaned.includes('-')) {
      const parts = cleaned.split('-');
      if (parts.length === 3) {
        day = parseInt(parts[0]);
        month = parseInt(parts[1]) - 1;
        year = parseInt(parts[2]);
        const date = new Date(year, month, day);
        if (!isNaN(date.getTime())) return date;
      }
    }
    
    // Format: DD.MM.YYYY
    if (cleaned.includes('.')) {
      const parts = cleaned.split('.');
      if (parts.length === 3) {
        day = parseInt(parts[0]);
        month = parseInt(parts[1]) - 1;
        year = parseInt(parts[2]);
        const date = new Date(year, month, day);
        if (!isNaN(date.getTime())) return date;
      }
    }
    
    // Try direct Date parsing
    const directDate = new Date(cleaned);
    if (!isNaN(directDate.getTime())) return directDate;
    
    return null;
  };

  // Get filtered and sorted stocks - with default sorting by Reco Date
  const filteredAndSortedStocks = useMemo(() => {
    let filtered = [...processedData.stocks];
    
    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(stock => {
        const stockData = stock.data;
        return Object.values(stockData).some(value => 
          value && value.toString().toLowerCase().includes(term)
        );
      });
    }
    
    // Apply sorting - default to RECO DATE if no sort key is set
    const sortKey = sortConfig.key || 'RECO DATE';
    const sortDirection = sortConfig.direction || 'desc';
    
    // Find the actual header name in the stock data that matches our sort key
    let actualSortKey = null;
    if (sortKey === 'RECO DATE') {
      actualSortKey = Object.keys(filtered[0]?.data || {}).find(key => 
        normalizeHeaderName(key).toUpperCase().includes('RECO') && 
        normalizeHeaderName(key).toUpperCase().includes('DATE')
      );
    } else {
      actualSortKey = Object.keys(filtered[0]?.data || {}).find(key => 
        normalizeHeaderName(key).toUpperCase() === normalizeHeaderName(sortKey).toUpperCase()
      );
    }
    
    if (actualSortKey) {
      filtered.sort((a, b) => {
        let aVal = a.data[actualSortKey] || '';
        let bVal = b.data[actualSortKey] || '';
        
        // Special handling for date fields
        if (sortKey === 'RECO DATE') {
          const aDate = parseDate(aVal);
          const bDate = parseDate(bVal);
          
          if (aDate && bDate) {
            return sortDirection === 'desc' ? bDate - aDate : aDate - bDate;
          }
          // If one date is invalid, put valid dates first
          if (aDate && !bDate) return sortDirection === 'desc' ? -1 : 1;
          if (!aDate && bDate) return sortDirection === 'desc' ? 1 : -1;
          // If both invalid, fall back to string comparison
          const aStr = aVal.toString().toLowerCase();
          const bStr = bVal.toString().toLowerCase();
          if (aStr < bStr) return sortDirection === 'desc' ? 1 : -1;
          if (aStr > bStr) return sortDirection === 'desc' ? -1 : 1;
          return 0;
        }
        
        // Try numeric comparison for numeric fields
        const aNum = parseFloat(aVal.toString().replace(/[₹%,]/g, '')) || 0;
        const bNum = parseFloat(bVal.toString().replace(/[₹%,]/g, '')) || 0;
        
        if (aNum !== 0 || bNum !== 0) {
          if (aNum < bNum) return sortDirection === 'desc' ? 1 : -1;
          if (aNum > bNum) return sortDirection === 'desc' ? -1 : 1;
          return 0;
        }
        
        // Fall back to string comparison
        const aStr = aVal.toString().toLowerCase();
        const bStr = bVal.toString().toLowerCase();
        if (aStr < bStr) return sortDirection === 'desc' ? 1 : -1;
        if (aStr > bStr) return sortDirection === 'desc' ? -1 : 1;
        return 0;
      });
    } else {
      // If no matching sort key found, sort by original row index as fallback
      filtered.sort((a, b) => (a.originalRowIndex || 0) - (b.originalRowIndex || 0));
    }
    
    return filtered;
  }, [processedData.stocks, searchTerm, sortConfig]);

  // Get filtered IPOs
  const filteredIPOs = useMemo(() => {
    let filtered = [...processedData.ipos];
    
    // Apply search filter
    if (ipoSearchTerm) {
      const term = ipoSearchTerm.toLowerCase();
      filtered = filtered.filter(ipo => {
        const ipoData = ipo.data;
        return Object.values(ipoData).some(value => 
          value && value.toString().toLowerCase().includes(term)
        );
      });
    }
    
    return filtered;
  }, [processedData.ipos, ipoSearchTerm]);

  // Handle sort click
  const handleSort = (header) => {
    setSortConfig(prev => ({
      key: header,
      direction: prev.key === header && prev.direction === 'desc' ? 'asc' : 'desc'
    }));
  };

  // Clear all filters and reset to default sorting
  const clearAllFilters = () => {
    setSearchTerm('');
    setIpoSearchTerm('');
    setSortConfig({ key: 'RECO DATE', direction: 'desc' });
  };

  // Get available sortable headers (numeric fields and date fields)
  const getSortableHeaders = () => {
    return columnHeaders.filter(header => {
      const normalized = normalizeHeaderName(header).toUpperCase();
      return (
        normalized.includes('PRICE') ||
        normalized.includes('CMP') ||
        normalized.includes('RETURN') ||
        normalized.includes('YIELD') ||
        normalized.includes('DATE') ||
        normalized.includes('S.NO') ||
        normalized.includes('SNO')
      );
    });
  };

  // ==================== LOGIN PROTECTION RENDER LOGIC ====================
  if (checkingLogin) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Checking authentication...</p>
        </div>
      </div>
    );
  }

  if (!isLoggedIn) {
    return (
      <>
        <Header />
        <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white relative">
          <div className="absolute inset-0 backdrop-blur-[5px] bg-white/80 z-10 flex items-center justify-center">
            <motion.div 
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full mx-4 border border-gray-200"
            >
              <div className="text-center mb-6">
                <div className="w-20 h-20 bg-gradient-to-r from-blue-100 to-blue-50 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FaLock className="text-3xl text-blue-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Authentication Required</h2>
                <p className="text-gray-600 mb-1">You need to log in to access the Dividend Growth Portfolio.</p>
                <p className="text-sm text-gray-500 mb-6">This content is exclusive to registered members.</p>
                <Link to="/login">
                  <button className="w-full py-3.5 px-6 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-300 flex items-center justify-center gap-3">
                    <FaSignInAlt className="text-lg" />
                    Go to Login Page
                  </button>
                </Link>
                <div className="mt-6 pt-6 border-t border-gray-100">
                  <p className="text-sm text-gray-500">
                    Don't have an account?{' '}
                    <Link to="/register" className="font-medium text-blue-600 hover:text-blue-500">
                      Sign up here
                    </Link>
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
        <Footer />
      </>
    );
  }
  
  if (contentAccess.DividendGrowthPortfolio !== 1) {
    return (
      <>
        <Header />
        <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white relative">
          <div className="absolute inset-0 backdrop-blur-[5px] bg-white/80 z-10 flex items-center justify-center">
            <motion.div 
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full mx-4 border border-gray-200"
            >
              <div className="text-center mb-6">
                <div className="w-20 h-20 bg-gradient-to-r from-amber-100 to-amber-50 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FaLock className="text-3xl text-amber-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Content Access Restricted</h2>
                <p className="text-gray-600 mb-3">
                  Hello <span className="font-semibold text-blue-600">{userData?.username}</span>! 
                  You don't have access to the Dividend Growth Portfolio yet.
                </p>
                <p className="text-sm text-gray-500 mb-6">
                  This premium content requires special access permissions.
                </p>
                <div className="space-y-3">
                  <a 
                    href="tel:+919319745997"
                    className="w-full py-3.5 px-6 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-300 flex items-center justify-center gap-3"
                  >
                    <FaPhone className="text-lg" />
                    Call Us: +91 9319745997
                  </a>
                  <a 
                    href="mailto:support@marketmaestroo.com"
                    className="w-full py-3.5 px-6 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-300 flex items-center justify-center gap-3"
                  >
                    <FaEnvelope className="text-lg" />
                    Email: support@marketmaestroo.com
                  </a>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  // ==================== MAIN RENDER LOGIC ====================
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading Dividend Growth Portfolio...</p>
          <p className="text-sm text-gray-500 mt-2">Auto-refresh every 60 seconds</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex items-center justify-center">
        <div className="text-center p-8 bg-white rounded-xl shadow-lg border border-red-100 max-w-md">
          <FaExclamationTriangle className="text-red-500 text-4xl mx-auto mb-4" />
          <h3 className="text-xl font-bold text-gray-900 mb-2">Error Loading Portfolio</h3>
          <p className="text-gray-600 mb-4">{error}</p>
          <button 
            onClick={fetchPortfolioData}
            className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors flex items-center gap-2 mx-auto"
          >
            <FaSync />
            Retry
          </button>
        </div>
      </div>
    );
  }

  const sortableHeaders = getSortableHeaders();

  return (
    <>
      <Header />
        <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
              <div className="flex flex-col md:flex-row items-center justify-between">
                <div>
                  <h1 className="text-3xl md:text-4xl font-bold mb-2">{pageMetadata.page_names}</h1>
                  <p className="text-blue-100">{pageMetadata.description}</p>
                  <p className="text-blue-200 text-sm mt-1">SEBI Registered Research Analyst (INH000021757)</p>
                </div>
                <div className="mt-4 md:mt-0 flex flex-col md:flex-row items-center gap-4">
                  <div className="flex items-center gap-3 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
                    <FaShieldAlt className="text-yellow-300" />
                    <span className="font-semibold">SEBI INH000021757</span>
                  </div>
                  <button
                    onClick={fetchPortfolioData}
                    className="flex items-center gap-2 bg-white/20 hover:bg-white/30 px-4 py-2 rounded-lg transition-colors"
                  >
                    <FaSync className="text-sm" />
                    Refresh Now
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            {/* Search and Filter Controls */}
            <div className="mb-8">
              <div className="flex flex-col md:flex-row gap-4 mb-4">
                <div className="flex-1">
                  <div className="relative">
                    <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search stocks..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-3 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent shadow-sm"
                    />
                  </div>
                </div>
                <button
                  onClick={clearAllFilters}
                  className="px-4 py-3 rounded-lg transition-colors flex items-center gap-2 bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-300"
                >
                  <FaSync className="text-sm" />
                  Clear Filters
                </button>
              </div>

              {/* Sort Controls - Only show sortable headers */}
              <div className="flex items-center gap-4 bg-white p-4 rounded-lg border border-gray-200 shadow-sm flex-wrap">
                <span className="text-gray-600 font-medium whitespace-nowrap">Sort by:</span>
                <div className="flex gap-2 flex-wrap">
                  {sortableHeaders.map(header => (
                    <button
                      key={header}
                      onClick={() => handleSort(header)}
                      className={`px-3 py-2 rounded-lg flex items-center gap-2 transition-colors whitespace-nowrap ${
                        sortConfig.key === header 
                          ? 'bg-blue-100 text-blue-700 border border-blue-200' 
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200 border border-gray-300'
                      }`}
                    >
                      {formatHeaderName(header)}
                      {sortConfig.key === header && (
                        sortConfig.direction === 'desc' ? <FaSortAmountDown /> : <FaSortAmountUp />
                      )}
                    </button>
                  ))}
                </div>
               
                <div className="ml-auto text-sm text-gray-500 whitespace-nowrap">
                  {filteredAndSortedStocks.length} stocks • {columnHeaders.length} columns
                </div>
              </div>
            </div>

            {/* Stocks Cards - Completely Dynamic */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              {filteredAndSortedStocks.length === 0 ? (
                <div className="text-center py-12 bg-white rounded-2xl shadow-sm border border-gray-200">
                  <FaChartLine className="text-gray-400 text-4xl mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-gray-700 mb-2">No Stocks Found</h3>
                  <p className="text-gray-500">Try adjusting your search criteria</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {filteredAndSortedStocks.map((stock, index) => {
                    const stockData = stock.data;
                    const headers = Object.keys(stockData);
                    
                    // Find return value for highlighting
                    const returnHeader = headers.find(h => 
                      normalizeHeaderName(h).toUpperCase().includes('RETURN')
                    );
                    const returnValue = returnHeader ? stockData[returnHeader] : '';
                    const returnNum = parseFloat(returnValue?.toString().replace(/[%,]/g, '')) || 0;
                    const isPositive = returnNum > 0;
                    
                    // Get stock name and ticker
                    const stockNameHeader = headers.find(h => 
                      normalizeHeaderName(h).toUpperCase().includes('STOCK') && 
                      normalizeHeaderName(h).toUpperCase().includes('NAME')
                    );
                    const tickerHeader = headers.find(h => 
                      normalizeHeaderName(h).toUpperCase().includes('TICKER')
                    );
                    
                    const stockName = stockNameHeader ? stockData[stockNameHeader] : '';
                    const ticker = tickerHeader ? stockData[tickerHeader] : '';
                    
                    return (
                      <motion.div
                        key={stock.id}
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.3, delay: index * 0.05 }}
                        whileHover={{ y: -4 }}
                        className="group relative"
                      >
                        {/* New Badge - Display as diagonal ribbon in top right corner */}
                        {stock.isNew && (
                          <div className="absolute top-0 right-0 z-20 overflow-hidden w-24 h-24">
                            <div 
                              className="absolute top-10 transform -translate-x-1/2 -translate-y-1/2 rotate-45 bg-gradient-to-r from-green-500 to-emerald-600 text-white text-xs font-bold py-1 px-6 shadow-lg"
                              style={{ right: "-57px", top: "18px" }}
                            >
                              NEW
                            </div>
                          </div>
                        )}

                        <div className="bg-gradient-to-br from-white to-gray-50 rounded-2xl border border-gray-200 transition-all duration-300 overflow-hidden hover:border-blue-300 hover:shadow-xl h-full">
                          <div className="p-5">
                            {/* Stock Header */}
                            <div className="mb-4">
                              {stockName && (
                                <h3 className="font-bold text-gray-900 text-lg line-clamp-2 group-hover:text-blue-600 transition-colors mb-1">
                                  {stockName}
                                </h3>
                              )}
                              {ticker && (
                                <div className="flex items-center mt-1">
                                  <span className="text-sm font-medium text-blue-600 bg-blue-50 px-2 py-0.5 rounded">
                                    {ticker}
                                  </span>
                                  <span className="ml-2 text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
                                    ID: {stock.id}
                                  </span>
                                </div>
                              )}
                            </div>

                            {/* Dynamic Data Grid - Skip NEW column from display */}
                            <div className="space-y-3">
                              {headers.map(header => {
                                const normalizedHeader = normalizeHeaderName(header);
                                const headerUpper = normalizedHeader.toUpperCase();
                                const value = stockData[header];
                                
                                // Skip header rows already shown and skip NEW column
                                if (headerUpper.includes('STOCK') && headerUpper.includes('NAME')) return null;
                                if (headerUpper.includes('TICKER')) return null;
                                if (headerUpper.includes('S.NO') || headerUpper.includes('SNO')) return null;
                                if (headerUpper === 'NEW') return null;
                                if (!value || value.trim() === '') return null;
                                
                                // Special styling for key metrics
                                const isReturn = headerUpper.includes('RETURN');
                                const isPrice = headerUpper.includes('PRICE') || headerUpper.includes('CMP');
                                const isDate = headerUpper.includes('DATE');
                                
                                let bgColor = 'bg-gray-50';
                                let textColor = 'text-gray-900';
                                
                                if (isReturn) {
                                  bgColor = isPositive ? 'bg-green-50' : 'bg-red-50';
                                  textColor = isPositive ? 'text-green-600' : 'text-red-600';
                                } else if (isPrice) {
                                  bgColor = 'bg-blue-50';
                                  textColor = 'text-blue-700';
                                } else if (isDate) {
                                  bgColor = 'bg-purple-50';
                                  textColor = 'text-purple-700';
                                }
                                
                                return (
                                  <div key={header} className={`p-3 rounded-lg ${bgColor}`}>
                                    <div className="flex justify-between items-center">
                                      <div className="text-xs font-medium text-gray-600">
                                        {formatHeaderName(header)}
                                      </div>
                                      <div className={`font-bold ${textColor} flex items-center gap-1`}>
                                        {isReturn && (
                                          isPositive ? <FaArrowUp className="text-sm" /> : <FaArrowDown className="text-sm" />
                                        )}
                                        {isDate && <FaCalendarAlt className="text-sm mr-1" />}
                                        {isPrice && <FaRupeeSign className="text-xs mr-0.5" />}
                                        {formatValue(value, header)}
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>

                            
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              )}
            </motion.div>

            {/* Research Reports Section - Display only if there are research reports */}
            {processedData.researchReports.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="mt-12"
              >
                <div className="bg-gradient-to-r from-white to-blue-50 rounded-2xl border border-gray-200 overflow-hidden shadow-sm">
                  <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-4">
                    <h3 className="text-xl font-bold text-white flex items-center gap-2">
                      <FaFilePdf className="text-red-300 text-2xl" />
                      Research Reports
                      <span className="text-sm bg-white/20 px-2 py-1 rounded-full ml-2">
                        {processedData.researchReports.length} Reports
                      </span>
                    </h3>
                    <p className="text-blue-100 text-sm mt-1">
                      In-depth analysis and research reports from Market Maestroo
                    </p>
                  </div>
                  
                  <div className="p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                      {processedData.researchReports.map((report, index) => {
                        const getDriveFileId = (url) => {
                          const patterns = [
                            /\/d\/([a-zA-Z0-9_-]+)/,
                            /id=([a-zA-Z0-9_-]+)/,
                            /file\/d\/([a-zA-Z0-9_-]+)/
                          ];
                          for (const pattern of patterns) {
                            const match = url.match(pattern);
                            if (match) return match[1];
                          }
                          return null;
                        };
                        
                        const fileId = getDriveFileId(report.reportLink);
                        const thumbnailUrl = fileId ? `https://drive.google.com/thumbnail?id=${fileId}&sz=w200` : null;
                        
                        return (
                          <motion.a
                            key={index}
                            href={report.reportLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.3, delay: index * 0.1 }}
                            whileHover={{ y: -4, scale: 1.02 }}
                            className="group block bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-xl transition-all duration-300"
                          >
                            <div className="relative h-32 bg-gradient-to-br from-gray-50 to-gray-100 overflow-hidden">
                              {thumbnailUrl ? (
                                <img 
                                  src={thumbnailUrl} 
                                  alt={report.companyName}
                                  className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity duration-300"
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                    e.target.parentElement.innerHTML = `
                                      <div className="flex items-center justify-center h-full">
                                        <div className="text-center">
                                          <svg className="w-12 h-12 text-gray-400 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"></path>
                                          </svg>
                                          <p className="text-xs text-gray-500">PDF Document</p>
                                        </div>
                                      </div>
                                    `;
                                  }}
                                />
                              ) : (
                                <div className="flex items-center justify-center h-full">
                                  <div className="text-center">
                                    <FaFilePdf className="w-12 h-12 text-red-400 mx-auto mb-2" />
                                    <p className="text-xs text-gray-500">PDF Report</p>
                                  </div>
                                </div>
                              )}
                              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                                <span className="bg-white/90 text-gray-800 px-3 py-1.5 rounded-lg text-sm font-medium flex items-center gap-2 shadow-lg">
                                  <FaExternalLinkAlt className="text-blue-600" />
                                  View Report
                                </span>
                              </div>
                            </div>
                            
                            <div className="p-4">
                              <h4 className="font-bold text-gray-900 text-base mb-1 group-hover:text-blue-600 transition-colors line-clamp-2">
                                {report.companyName}
                              </h4>
                              <div className="flex items-center justify-between mt-3">
                                <div className="flex items-center gap-2 text-xs text-gray-500">
                                  <FaCalendarAlt className="text-gray-400" />
                                  <span>{report.reportDate}</span>
                                </div>
                                <span className="text-xs font-medium text-blue-600 bg-blue-50 px-2 py-1 rounded-full flex items-center gap-1">
                                  <FaDownload className="text-xs" />
                                  Open PDF
                                </span>
                              </div>
                              {report.additionalInfo && (
                                <p className="text-xs text-gray-500 mt-2 line-clamp-1">
                                  {report.additionalInfo}
                                </p>
                              )}
                            </div>
                          </motion.a>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Google Form Section - Display only if Google Form exists */}
            {processedData.googleForm && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.15 }}
                className="mt-12"
              >
                <div className="bg-gradient-to-r from-purple-50 to-purple-50 rounded-2xl border border-purple-200 overflow-hidden shadow-sm">
                  <div className="bg-gradient-to-r from-purple-600 to-purple-600 px-6 py-4 flex items-center justify-between">
                    <div>
                      <h3 className="text-xl font-bold text-white flex items-center gap-2">
                        <FaGoogle className="text-white text-2xl" />
                        {processedData.googleForm.title}
                      </h3>
                    </div>
                    <div>
                      <a
                        href={processedData.googleForm.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group flex-shrink-0"
                      >
                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          className="px-6 py-3 bg-gradient-to-r from-purple-500 to-purple-500 hover:from-purple-600 hover:to-purple-600 text-white font-semibold rounded-lg shadow-md hover:shadow-lg transition-all duration-300 flex items-center gap-3"
                        >
                          <FaGoogle className="text-lg" />
                          Fill Google Form
                          <FaArrowRight className="text-sm group-hover:translate-x-1 transition-transform" />
                        </motion.button>
                      </a>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Disclosures Section - ALWAYS show if available */}
            {processedData.disclosures.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="mt-12"
              >
                <div className="bg-gradient-to-r from-gray-50 to-white rounded-2xl border border-gray-200 p-8">
                  <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                    <FaShieldAlt className="text-blue-500" />
                    Regulatory Disclosures
                  </h3>
                  
                  <div className="space-y-6">
                    {processedData.disclosures.map((disclosure, index) => {
                      let title = '';
                      let icon = null;
                      let color = 'text-gray-700';
                      
                      if (disclosure.type === 'registration') {
                        title = 'SEBI Registration';
                        icon = <FaShieldAlt className="text-blue-500" />;
                        color = 'text-blue-700';
                      } else if (disclosure.type === 'risk') {
                        title = 'Risk Disclosure';
                        icon = <FaExclamationTriangle className="text-amber-500" />;
                        color = 'text-amber-700';
                      } else if (disclosure.type === 'disclaimer') {
                        title = 'Disclaimer';
                        icon = <FaExclamationTriangle className="text-gray-500" />;
                        color = 'text-gray-700';
                      } else if (disclosure.type === 'caution') {
                        title = 'Investor Caution';
                        icon = <FaLock className="text-red-500" />;
                        color = 'text-red-700';
                      }
                      
                      return (
                        <div key={index} className="bg-white p-5 rounded-xl border border-gray-200">
                          <div className="flex items-start gap-3 mb-3">
                            {icon}
                            <h4 className={`font-bold ${color}`}>{title}</h4>
                          </div>
                          <p className="text-gray-600 whitespace-pre-line text-sm leading-relaxed">
                            {disclosure.text}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </motion.div>
            )}

            {/* Footer */}
            <div className="mt-8 text-center text-sm text-gray-500">
              {lastUpdated && (
                <p className="mt-1">Last refreshed: {lastUpdated.toLocaleString()}</p>
              )}
              <p className="mt-2 text-xs bg-gray-100 inline-block px-3 py-1 rounded-full">
                Market Maestroo Private Limited | SEBI Registration No: INH000021757 | Principal Officer: Kritika Yadav
              </p>
              {columnHeaders.length > 0 && (
                <div className="mt-3 text-xs text-gray-400" />
              )}
            </div>
          </div>
        </div>
      <Footer />
    </>
  );
};

export default DividendGrowthPortfolio;